import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'
import { base_url } from '../../utils/config'

// ── Thunks ───────────────────────────────────────────────────

export const recordProductView = createAsyncThunk(
  'productActivity/recordView',
  async (productId, { rejectWithValue }) => {
    try {
      await axios.post(`${base_url}/api/product-activity/${productId}/view`)
      return productId
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

export const recordProductSwap = createAsyncThunk(
  'productActivity/recordSwap',
  async (productId, { rejectWithValue }) => {
    try {
      await axios.post(`${base_url}/api/product-activity/${productId}/swap`)
      return productId
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

export const recordProductGenerate = createAsyncThunk(
  'productActivity/recordGenerate',
  async ({ productId, creditsUsed }, { rejectWithValue }) => {
    try {
      await axios.post(
        `${base_url}/api/product-activity/${productId}/generate`,
        { creditsUsed }
      )
      return { productId, creditsUsed }
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

export const recordProductDownload = createAsyncThunk(
  'productActivity/recordDownload',
  async (productId, { rejectWithValue }) => {
    try {
      await axios.post(`${base_url}/api/product-activity/${productId}/download`)
      return productId
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

export const recordProductShare = createAsyncThunk(
  'productActivity/recordShare',
  async (productId, { rejectWithValue }) => {
    try {
      await axios.post(`${base_url}/api/product-activity/${productId}/share`)
      return productId
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

export const fetchProductStats = createAsyncThunk(
  'productActivity/fetchStats',
  async (productId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${base_url}/api/product-activity/${productId}`
      )
      return data
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

// ── Slice ────────────────────────────────────────────────────

const initialState = {
  stats: null,
  statsLoading: false,
  statsError: null,

  actionLoading: false,
  actionError: null,
}

const productActivitySlice = createSlice({
  name: 'productActivity',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchProductStats.pending, state => {
        state.statsLoading = true
        state.statsError = null
      })
      .addCase(fetchProductStats.fulfilled, (state, action) => {
        state.statsLoading = false
        state.stats = action.payload
      })
      .addCase(fetchProductStats.rejected, (state, action) => {
        state.statsLoading = false
        state.statsError = action.payload
      })

    const recordActions = [
      recordProductView,
      recordProductSwap,
      recordProductGenerate,
      recordProductDownload,
      recordProductShare
    ]
    recordActions.forEach(thunk => {
      builder
        .addCase(thunk.pending, state => {
          state.actionLoading = true
          state.actionError = null
        })
        .addCase(thunk.fulfilled, state => {
          state.actionLoading = false
        })
        .addCase(thunk.rejected, (state, action) => {
          state.actionLoading = false
          state.actionError = action.payload
        })
    })
  }
})

// ── Helper to dispatch the right thunk based on actionType ──
export const trackProductAction = ({ productId, actionType, metadata = {} }) => dispatch => {
  switch (actionType) {
    case 'view':
      return dispatch(recordProductView(productId))
    case 'swap':
      return dispatch(recordProductSwap(productId))
    case 'generate':
      return dispatch(recordProductGenerate({ productId, creditsUsed: metadata.creditsUsed }))
    case 'download':
      return dispatch(recordProductDownload(productId))
    case 'share':
      return dispatch(recordProductShare(productId))
    default:
      return Promise.resolve()
  }
}

export default productActivitySlice.reducer
